/**
 * Partner login & týdenní objednávky — pro firmy s rozvozem obědů.
 * Závisí na ?partner v URL — když je tam, ukáže se Partner sekce, jinak nic.
 */

import { useEffect, useMemo, useState } from 'react'
import { apiUrl } from '../lib/api'

const CZ_DAYS_LONG = ['Pondělí','Úterý','Středa','Čtvrtek','Pátek']
const CZ_DAYS_SHORT = ['Po','Út','St','Čt','Pá']

type Partner = { id: number; name: string; contact_person?: string; contact_phone?: string; contact_email?: string; price_per_meal_czk: number }
type MenuDay = {
  day: string; dow: number
  soup: { id: number; title: string } | null
  mains: { id: number; title: string; count: number; locked: boolean }[]
}
type WeekMenu = { week_start: string; locked: boolean; closeup_at: string; days: MenuDay[] }

const API = apiUrl('/api/sobacov')  // partner portál volá backend — absolutně, web běží jinde

export function PartnerPortal() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem('sobacov_partner_token'))
  const [partner, setPartner] = useState<Partner | null>(() => {
    const raw = sessionStorage.getItem('sobacov_partner_obj')
    return raw ? JSON.parse(raw) : null
  })

  function logout() {
    sessionStorage.removeItem('sobacov_partner_token')
    sessionStorage.removeItem('sobacov_partner_obj')
    setToken(null); setPartner(null)
  }

  if (!token || !partner) return <PartnerLogin onLogin={(t, p) => {
    sessionStorage.setItem('sobacov_partner_token', t)
    sessionStorage.setItem('sobacov_partner_obj', JSON.stringify(p))
    setToken(t); setPartner(p)
  }} />

  return <PartnerDashboard token={token} partner={partner} onLogout={logout} />
}

/* ─────────── PIN login ─────────── */

function PartnerLogin({ onLogin }: { onLogin: (token: string, p: Partner) => void }) {
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function press(d: string) {
    if (busy) return
    setErr(null)
    if (d === '←') { setPin((p) => p.slice(0, -1)); return }
    const np = (pin + d).slice(0, 6)
    setPin(np)
    if (np.length === 6) setTimeout(() => tryLogin(np), 100)
  }

  async function tryLogin(p: string) {
    setBusy(true); setErr(null)
    try {
      const r = await fetch(`${API}/partner/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: p }),
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.detail || 'Špatný PIN')
      }
      const j = await r.json()
      onLogin(j.token, j.partner)
    } catch (e: any) {
      setErr(e.message); setPin('')
    } finally { setBusy(false) }
  }

  return (
    <section className="min-h-[80vh] bg-white py-12 md:py-20">
      <div className="container-x max-w-md mx-auto text-center">
        <p className="h-eyebrow">Partner login</p>
        <h1 className="h-display text-3xl md:text-4xl mt-4">Firemní obědy</h1>
        <span className="red-rule mt-5 mx-auto" />
        <p className="mt-5 text-ink-700">
          Zadejte svůj 6místný PIN pro přístup k týdennímu menu a objednávkám pro vaši firmu.
        </p>

        {/* PIN displej */}
        <div className="flex justify-center gap-4 my-8">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`w-3 h-3 transition ${pin.length > i ? 'bg-brand-500 scale-125' : 'bg-ink-900/15'}`}
            />
          ))}
        </div>

        {err && (
          <div className="bg-brand-50 border border-brand-200 text-brand-600 px-4 py-2 text-xs uppercase tracking-wider font-semibold mb-4 inline-block">
            {err}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
          {['1','2','3','4','5','6','7','8','9','','0','←'].map((d, i) => (
            d ? (
              <button
                key={i} onClick={() => press(d)} disabled={busy}
                className="aspect-square bg-white border border-ink-900/15 text-3xl font-light text-ink-900 active:bg-brand-50 active:border-brand-500 transition disabled:opacity-50"
              >{d}</button>
            ) : <div key={i} />
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-ink-900/10 text-sm text-ink-700">
          <p>
            <b>Nemáte PIN?</b> Napište nám a domluvíme se na pravidelném rozvozu obědů
            pro vaše zaměstnance.
          </p>
          <a href="#kontakt" className="text-brand-500 underline mt-2 inline-block font-semibold">
            Kontaktovat nás →
          </a>
        </div>

        {/* Reklamní sekce */}
        <PartnerPromo />
      </div>
    </section>
  )
}

/* ─────────── Reklamní sekce ─────────── */

function PartnerPromo() {
  return (
    <div className="mt-16 text-left">
      <p className="h-eyebrow">Pro firmy</p>
      <h2 className="h-display text-2xl md:text-3xl mt-3">
        Vlastní firemní appka — zdarma
      </h2>
      <span className="red-rule mt-4" />
      <p className="mt-5 text-ink-800 text-base leading-relaxed">
        Pokud bude mít vaše firma zájem, vyrobíme pro každého zaměstnance vlastní mobilní appku
        s týdenním menu. Zaměstnanci si sami označí co chtějí k obědu, a appka pošle souhrn
        kuchařům automaticky vždy ve čtvrtek 22:00. Zjednodušení administrace, žádné excely.
      </p>

      {/* Mockup screenshoty 3 obrazovek — reálné podle NFI Metall PWA */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10">
        <PhoneMockup title="Týdenní menu" subtitle="Závazný výběr do čtvrtka">
          <ScreenMenu />
        </PhoneMockup>
        <PhoneMockup title="Docházka" subtitle="Příchod & odchod přes GPS">
          <ScreenAttendance />
        </PhoneMockup>
        <PhoneMockup title="Volno" subtitle="Dovolená, nemocenská, osobní">
          <ScreenPayout />
        </PhoneMockup>
      </div>

      <ul className="mt-10 space-y-2 text-sm text-ink-800">
        {[
          'PIN přístup per firma — jeden účet vidí jen své objednávky',
          'Týdenní menu předem — vždy závazné do čtvrtka 22:00',
          'Automatický souhrn pro naše kuchaře',
          'Fakturace měsíčně — 1× faktura za všechny zaměstnance',
          'Bez měsíčních poplatků — appka je zahrnuta v ceně rozvozu',
        ].map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <span className="text-brand-500 font-bold">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex gap-2 flex-wrap">
        <a href="tel:+420606738051" className="btn-book text-xs">+420 606 738 051</a>
        <a href="#kontakt" className="btn-outline-dark text-xs">Nezávazná poptávka</a>
      </div>
    </div>
  )
}

/* ─────────── Phone mockup wrapper ─────────── */

function PhoneMockup({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="group flex flex-col items-center">
      <div
        className="relative bg-ink-900 rounded-[32px] shadow-[0_25px_50px_-12px_rgba(16,43,69,0.45)] hover:shadow-[0_30px_60px_-12px_rgba(229,2,69,0.35)] hover:-translate-y-1 transition-all duration-300 ring-1 ring-ink-900/20"
        style={{ width: 220, padding: 8 }}
      >
        {/* Notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-4 bg-ink-900 rounded-b-2xl z-10" />
        {/* Side button */}
        <div className="absolute -right-0.5 top-20 w-0.5 h-12 bg-ink-900 rounded-r-sm" />
        <div className="absolute -left-0.5 top-16 w-0.5 h-7 bg-ink-900 rounded-l-sm" />
        <div className="absolute -left-0.5 top-28 w-0.5 h-12 bg-ink-900 rounded-l-sm" />
        {/* Screen */}
        <div className="bg-white rounded-[24px] overflow-hidden" style={{ height: 400 }}>
          {children}
        </div>
      </div>
      <div className="mt-5 text-center">
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-500 font-bold">{title}</div>
        <div className="font-semibold text-ink-900 text-sm mt-1">{subtitle}</div>
      </div>
    </div>
  )
}

/* ─────────── Mockup obrazovky ─────────── */

/* Reálné mockupy z NFI Metall PWA — dark mode, NFI METALL logo nahoře, 6-tab bottom bar */

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-3 pt-2 pb-1 text-white text-[9px] font-bold">
      <div className="flex items-center gap-1.5">
        <div className="bg-white text-ink-900 px-1 py-px text-[7px] font-bold tracking-tight rounded-sm">NFI</div>
        <span>9:14</span>
      </div>
      <div className="flex items-center gap-1">
        <span>•••</span>
        <span>📶</span>
        <span className="bg-emerald-500 text-ink-900 px-1 text-[8px] font-bold rounded-sm">100</span>
      </div>
    </div>
  )
}

function UserLine({ name }: { name: string }) {
  return (
    <div className="px-3 pb-2 text-white/90 text-[10px] font-semibold">{name}</div>
  )
}

function BottomTabs({ active }: { active: string }) {
  const tabs = [
    { id: 'dochazka', icon: '📍', label: 'Docházka' },
    { id: 'obedy',    icon: '🍽️', label: 'Obědy' },
    { id: 'sklad',    icon: '📦', label: 'Sklad' },
    { id: 'vyroba',   icon: '⚙️', label: 'Výroba' },
    { id: 'montaz',   icon: '🔧', label: 'Montáž' },
    { id: 'volno',    icon: '🏖️', label: 'Volno' },
  ]
  return (
    <div className="absolute bottom-0 left-0 right-0 bg-ink-900 border-t border-white/10 flex">
      {tabs.map((t) => (
        <div key={t.id} className="flex-1 py-1.5 text-center">
          <div className="text-[11px] leading-none">{t.icon}</div>
          <div className={`text-[6px] uppercase tracking-wider font-bold mt-0.5 ${
            t.id === active ? 'text-brand-500' : 'text-white/55'
          }`}>{t.label}</div>
        </div>
      ))}
    </div>
  )
}

function ScreenMenu() {
  const dishesPo = [
    { n: 1, name: '150g Hovězí štěpánská pečeně, jasmínová rýže', sel: false },
    { n: 2, name: '150g Smažené kuřecí řízečky, bramborová kaše', sel: true },
    { n: 3, name: '120g Zeleninový salát s grilovaným kuřecím masem', sel: false },
  ]
  return (
    <div className="relative h-full bg-ink-900 text-white">
      <StatusBar />
      <UserLine name="Robin Latal" />
      <div className="px-3 overflow-y-auto" style={{ maxHeight: 'calc(100% - 88px)' }}>
        {/* Pondělí */}
        <div className="border border-white/8 rounded-lg p-2.5 mb-2">
          <div className="font-bold text-[11px]">Po · 22.6.</div>
          <div className="text-white/55 text-[9px] mt-0.5">🥣 Špenátová s vejci</div>
          <div className="mt-2 space-y-1.5">
            {dishesPo.map((d) => (
              <div key={d.n} className={`rounded-lg p-1.5 flex items-start gap-1.5 ${
                d.sel ? 'bg-emerald-500 text-ink-900' : 'border border-white/10 text-white'
              }`}>
                <div className={`shrink-0 w-3.5 h-3.5 rounded-full grid place-items-center text-[7px] font-bold ${
                  d.sel ? 'bg-white/30 text-ink-900' : 'border border-white/20 text-white/70'
                }`}>{d.n}</div>
                <div className={`text-[8px] leading-tight flex-1 ${d.sel ? 'font-bold' : ''}`}>{d.name}</div>
                {d.sel && <div className="text-[9px] font-bold leading-none">✓</div>}
              </div>
            ))}
          </div>
        </div>
        {/* Úterý */}
        <div className="border border-white/8 rounded-lg p-2.5">
          <div className="font-bold text-[11px]">Út · 23.6.</div>
          <div className="text-white/55 text-[9px] mt-0.5">🥣 Uzená s kroupami</div>
          <div className="mt-2 space-y-1.5">
            <div className="rounded-lg p-1.5 border border-white/10 text-white flex items-start gap-1.5">
              <div className="shrink-0 w-3.5 h-3.5 rounded-full border border-white/20 grid place-items-center text-[7px] text-white/70">1</div>
              <div className="text-[8px] leading-tight">150g Krůtí medailonky na žampionech</div>
            </div>
            <div className="rounded-lg p-1.5 bg-emerald-500 text-ink-900 flex items-start gap-1.5">
              <div className="shrink-0 w-3.5 h-3.5 rounded-full bg-white/30 grid place-items-center text-[7px] font-bold">3</div>
              <div className="text-[8px] leading-tight font-bold">120g Tortila gyros s kuřecím masem</div>
              <div className="text-[9px] font-bold leading-none">✓</div>
            </div>
          </div>
        </div>
      </div>
      <BottomTabs active="obedy" />
    </div>
  )
}

function ScreenAttendance() {
  return (
    <div className="relative h-full bg-ink-900 text-white">
      <StatusBar />
      <UserLine name="Robin Latal" />
      <div className="px-3">
        <div className="font-bold text-base">Docházka</div>
        <div className="text-white/55 text-[9px] mt-0.5">Čtvrtek 25. Června</div>

        <div className="mt-3 inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[8px] text-white/85">GPS · ověřuje areál ±150 m</span>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <button className="bg-emerald-500 text-ink-900 rounded-lg py-3 flex flex-col items-center gap-1 shadow-[0_0_20px_rgba(16,185,129,0.4)]">
            <span className="w-3 h-3 rounded-full bg-emerald-300" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Příchod</span>
          </button>
          <button className="bg-ink-900 border border-white/15 text-white/65 rounded-lg py-3 flex flex-col items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-brand-600" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Odchod</span>
          </button>
        </div>

        <div className="mt-3 text-[8px] uppercase tracking-wider font-bold text-white/55">Dnes</div>
        <div className="mt-2 text-center text-[9px] text-white/40 py-2">Zatím žádné záznamy</div>

        <div className="mt-2 bg-white/5 border border-white/10 rounded-lg px-2.5 py-2 flex items-center justify-between text-[10px]">
          <span>📋 Moje docházka</span>
          <span className="text-white/55 text-[8px]">▸</span>
        </div>
      </div>
      <BottomTabs active="dochazka" />
    </div>
  )
}

function ScreenPayout() {
  return (
    <div className="relative h-full bg-ink-900 text-white">
      <StatusBar />
      <UserLine name="Robin Latal" />
      <div className="px-3 overflow-y-auto" style={{ maxHeight: 'calc(100% - 88px)' }}>
        <div className="font-bold text-base">🏖️ Dovolená / Nepřítomnost</div>

        {/* Nová žádost */}
        <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-2">
          <div className="text-[9px] font-bold">Nová žádost</div>
          <div className="grid grid-cols-3 gap-1 mt-2">
            <div className="bg-brand-500 text-white rounded-md py-1 text-center text-[8px] font-bold">🏖️ Dovolená</div>
            <div className="bg-white/5 border border-white/10 rounded-md py-1 text-center text-[8px] text-white/65">🤧 Nemocenská</div>
            <div className="bg-white/5 border border-white/10 rounded-md py-1 text-center text-[8px] text-white/65">📋 Osobní</div>
          </div>
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            <div>
              <div className="text-[7px] uppercase tracking-wider text-white/55 font-bold">OD</div>
              <div className="bg-white/5 border border-white/10 rounded-md py-2 mt-0.5" />
            </div>
            <div>
              <div className="text-[7px] uppercase tracking-wider text-white/55 font-bold">DO</div>
              <div className="bg-white/5 border border-white/10 rounded-md py-2 mt-0.5" />
            </div>
          </div>
          <div className="mt-1.5 bg-white/5 border border-white/10 rounded-md py-1.5 px-2 text-[8px] text-white/40">Poznámka (nepovinné)</div>
          <button className="mt-1.5 w-full bg-emerald-500 text-ink-900 rounded-md py-1.5 text-[9px] font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(16,185,129,0.4)]">
            Odeslat žádost
          </button>
        </div>

        {/* Moje žádosti */}
        <div className="mt-2 text-[7px] uppercase tracking-wider font-bold text-white/55">Moje žádosti</div>
        <div className="mt-1 space-y-1.5">
          <div className="bg-white/5 border border-white/10 border-l-2 border-l-amber-400 rounded-md p-1.5 flex items-center justify-between">
            <div>
              <div className="text-[9px] font-bold">Dovolená</div>
              <div className="text-[7px] text-white/55">4. 7. — 11. 7. · 5 dní</div>
            </div>
            <div className="text-[7px] uppercase font-bold text-amber-400">Čeká</div>
          </div>
          <div className="bg-white/5 border border-white/10 border-l-2 border-l-brand-500 rounded-md p-1.5 flex items-center justify-between">
            <div>
              <div className="text-[9px] font-bold">Dovolená</div>
              <div className="text-[7px] text-white/55">26. 5. — 29. 5. · 4 dní</div>
            </div>
            <div className="text-[7px] uppercase font-bold text-brand-500">Zamítnuto</div>
          </div>
        </div>
      </div>
      <BottomTabs active="volno" />
    </div>
  )
}

/* ─────────── Dashboard (po loginu) ─────────── */

function PartnerDashboard({ token, partner, onLogout }: { token: string; partner: Partner; onLogout: () => void }) {
  const [menu, setMenu] = useState<WeekMenu | null>(null)
  const [counts, setCounts] = useState<Record<string, number>>({})  // key: "dow-itemId"
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [err, setErr] = useState<string | null>(null)

  async function loadMenu() {
    setBusy(true); setErr(null)
    try {
      const r = await fetch(`${API}/partner/menu/week`, { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) {
        if (r.status === 401) return onLogout()
        throw new Error('Nepodařilo se načíst menu')
      }
      const j: WeekMenu = await r.json()
      setMenu(j)
      const initial: Record<string, number> = {}
      j.days.forEach((d) => d.mains.forEach((m) => { initial[`${d.dow}-${m.id}`] = m.count || 0 }))
      setCounts(initial)
    } catch (e: any) { setErr(e.message) }
    finally { setBusy(false) }
  }
  useEffect(() => { loadMenu() }, [])

  async function save() {
    if (!menu) return
    setSaving(true); setErr(null)
    try {
      const orders = Object.entries(counts).map(([key, count]) => {
        const [dow, mid] = key.split('-').map((n) => parseInt(n))
        return { day_of_week: dow, menu_item_id: mid, count }
      }).filter((o) => o.count > 0 || true)  // posíláme i 0 (= reset)

      const r = await fetch(`${API}/partner/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ week_start: menu.week_start, orders }),
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.detail || 'Nepodařilo se uložit')
      }
      setSavedAt(new Date())
    } catch (e: any) { setErr(e.message) }
    finally { setSaving(false) }
  }

  const totalMeals = useMemo(() =>
    Object.values(counts).reduce((s, n) => s + (n || 0), 0)
  , [counts])
  const totalPrice = totalMeals * (partner.price_per_meal_czk || 145)

  const countdownText = useMemo(() => {
    if (!menu) return ''
    const close = new Date(menu.closeup_at)
    const now = new Date()
    const diff = close.getTime() - now.getTime()
    if (diff < 0) return 'Uzavřeno'
    const h = Math.floor(diff / 3600000)
    const m = Math.floor((diff % 3600000) / 60000)
    if (h > 24) return `Uzavírá se za ${Math.floor(h/24)} dní ${h%24} hod`
    return `Uzavírá se za ${h} h ${m} min`
  }, [menu])

  return (
    <section className="min-h-[80vh] bg-white py-10">
      <div className="container-x max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <p className="h-eyebrow">Firemní obědy · {partner.name}</p>
            <h1 className="h-display text-2xl md:text-3xl mt-3">
              Týdenní menu
              {menu && <span className="text-ink-700/60 text-base font-normal ml-2">
                {czRange(menu.week_start)}
              </span>}
            </h1>
            {menu && (
              <p className="mt-2 text-sm text-ink-700">
                Cena za oběd: <b className="text-brand-500">{partner.price_per_meal_czk} Kč</b> · fakturujeme měsíčně
              </p>
            )}
          </div>
          <div className="text-right">
            <button onClick={onLogout} className="text-xs uppercase tracking-[0.10em] font-semibold text-ink-700 hover:text-brand-500">
              Odhlásit →
            </button>
            {menu && (
              <div className={`mt-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold ${
                menu.locked ? 'bg-brand-500 text-white' : 'bg-emerald-500 text-white'
              }`}>
                {menu.locked ? 'Týden je uzavřen' : countdownText}
              </div>
            )}
          </div>
        </div>

        {err && <div className="bg-brand-50 text-brand-600 p-3 mb-4 text-sm font-semibold">{err}</div>}
        {busy && <div className="text-ink-700 text-center py-8">Načítám menu…</div>}

        {/* Menu tabulka */}
        {menu && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-ink-900/20">
                  <th className="text-left p-3 font-semibold uppercase tracking-wider text-xs text-ink-700">Den</th>
                  <th className="text-left p-3 font-semibold uppercase tracking-wider text-xs text-ink-700">Polévka</th>
                  <th className="text-left p-3 font-semibold uppercase tracking-wider text-xs text-ink-700">Hlavní jídlo</th>
                  <th className="p-3 font-semibold uppercase tracking-wider text-xs text-ink-700 w-32 text-center">Počet</th>
                </tr>
              </thead>
              <tbody>
                {menu.days.map((d) => (
                  d.mains.map((m, i) => (
                    <tr key={`${d.dow}-${m.id}`} className="border-b border-ink-900/8 hover:bg-brand-50/40">
                      {i === 0 && (
                        <td rowSpan={d.mains.length} className="p-3 align-top border-r border-ink-900/8">
                          <div className="font-bold text-ink-900">{CZ_DAYS_LONG[d.dow]}</div>
                          <div className="text-xs text-ink-700">{czShort(d.day)}</div>
                        </td>
                      )}
                      {i === 0 && (
                        <td rowSpan={d.mains.length} className="p-3 align-top text-ink-800 border-r border-ink-900/8">
                          {d.soup ? d.soup.title : <span className="text-ink-700/40">—</span>}
                        </td>
                      )}
                      <td className="p-3 text-ink-800">{m.title}</td>
                      <td className="p-3 text-center">
                        <input
                          type="number"
                          min={0} max={200}
                          disabled={menu.locked || m.locked}
                          value={counts[`${d.dow}-${m.id}`] ?? 0}
                          onChange={(e) => setCounts({ ...counts, [`${d.dow}-${m.id}`]: parseInt(e.target.value) || 0 })}
                          className="w-20 border border-ink-900/15 px-2 py-1.5 text-center font-bold text-ink-900 focus:outline-none focus:border-brand-500 disabled:bg-ink-900/5"
                        />
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-brand-500 bg-brand-50/50">
                  <td colSpan={3} className="p-3 text-right font-bold text-ink-900 uppercase tracking-wider text-sm">
                    Celkem obědů
                  </td>
                  <td className="p-3 text-center font-bold text-brand-500 text-lg">{totalMeals}</td>
                </tr>
                <tr className="bg-brand-50/30">
                  <td colSpan={3} className="p-3 text-right text-ink-800 text-sm">
                    Předběžná částka <span className="text-xs text-ink-700">(fakturujeme měsíčně)</span>
                  </td>
                  <td className="p-3 text-center font-bold text-ink-900">{totalPrice.toLocaleString('cs-CZ')} Kč</td>
                </tr>
              </tfoot>
            </table>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-ink-700">
                {savedAt && (
                  <span className="text-emerald-600 font-semibold">
                    ✓ Uloženo {savedAt.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <button
                onClick={save}
                disabled={saving || menu.locked}
                className="btn-book disabled:opacity-40"
              >
                {saving ? 'Ukládám…' : menu.locked ? 'Týden uzavřen' : 'Uložit objednávky'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function czShort(iso: string) {
  const [, m, d] = iso.split('-')
  return `${parseInt(d)}.${parseInt(m)}.`
}
function czRange(weekStart: string) {
  const start = new Date(weekStart)
  const end = new Date(weekStart); end.setDate(end.getDate() + 4)
  return `${start.getDate()}.${start.getMonth()+1}. – ${end.getDate()}.${end.getMonth()+1}. ${end.getFullYear()}`
}
